import {useState} from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {ICoreState} from "../../../../data/redux/coreReducer";
import {useSelector} from "react-redux";
import {Formik, Form, Field} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useAuth} from '../core/Auth'
import {ILoginResponse} from '../core/_models'
import { post, setAuthToken, clearAuthToken } from '../../../../utils/ajax';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

const getLoginErrorMessage = (err: any): string => {
  const response = err?.response

  if (response) {
    const body = response.body
    if (typeof body === 'string' && body.trim()) return body
    if (body?.message) return body.message
    if (body?.error) return body.error
    if (response.status === 401 || response.status === 400) return 'The login details are incorrect'
    return `Request failed with status ${response.status}`
  }

  if (err?.timeout) return 'The request timed out. Please check your connection and try again.'

  return "Couldn't reach the server. Check your network connection and try again."
}

export const Login = () => {
  const [loading,setLoading]=useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const authState: ICoreState = useSelector((state: any) => state.core)
  // const {isLoading, user} = authState

  const handleSubmit = async (values: typeof initialValues, {setSubmitting, setStatus}: any) => {
    setLoading(true)
    post(`https://bnivs-api.laboremus.ug/api/auth/login`, {
        username: values.email,
        password: values.password,
      },
        (resp: ILoginResponse) => {
        setAuthToken(resp.accessToken, resp.tokenType)
        saveAuth({
          api_token: resp.accessToken,
          tokenType: resp.tokenType,
          expiresIn: resp.expiresIn,
        })
        setCurrentUser(resp.user)
        setSubmitting(false) // Ensure form is no longer submitting on success
        setLoading(false) // Stop loading on success
        },
        (err: any) => {
            clearAuthToken() // Clear the request token on error
            saveAuth(undefined) // Clear auth on error
            setCurrentUser(undefined) // Clear user on error
            setStatus(getLoginErrorMessage(err))
            setSubmitting(false)
            setLoading(false)
        }
      )
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loginSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Form className='form w-100' noValidate id='kt_login_signin_form'>
          {/* begin::Heading */}
          <div className='text-center mb-10'>
            <img style={{width: '100px'}} className='mb-5' src={toAbsoluteUrl('/assets/National_Bank_of_Uganda.jpg')} alt="Bank of Uganda" />
            <h1 className='text-dark mb-3'>National ID Verification System</h1>
            <div className='text-gray-400 fw-bold fs-6'>
              The gold standard in multi-modal trust.<br/> Bank-grade KYC & KYB, backed by NIRA biometric verification.
            </div>
          </div>

          <div className='fv-row mb-10'>
            <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
            <Field
              placeholder='john.doe@example.com'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {'is-invalid': formik.touched.email && formik.errors.email},
                {'is-valid': formik.touched.email && !formik.errors.email}
              )}
              type='email'
              name='email'
              autoComplete='off'
            />
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container text-danger'>
                <span role='alert' className='fv-help-block'>{formik.errors.email}</span>
              </div>
            )}
          </div>

          <div className='fv-row mb-10'>
            <label className='form-label fs-6 fw-bolder text-dark'>Password</label>
            <Field
              placeholder='**************'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {'is-invalid': formik.touched.password && formik.errors.password},
                {'is-valid': formik.touched.password && !formik.errors.password}
              )}
              type='password'
              name='password'
              autoComplete='off'
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container text-danger'>
                <span role='alert' className='fv-help-block'>{formik.errors.password}</span>
              </div>
            )}
          </div>

          <div className='text-center'>
            <button
              type='submit'
              id='kt_sign_in_submit'
              className='btn btn-lg btn-labo-primary w-100 mb-5'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>Sign In</span>}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Authenticating. Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>

          {formik.status && (
            <div className='mb-lg-5 alert bg-light-danger'>
              <div className='alert-text text-danger text-center'>{formik.status}</div>
            </div>
          )}

          <div className="text-center text-gray-500 fw-bold fs-8">Need an account? Reach out to the administrator.</div>
        </Form>
      )}
    </Formik>
  )
}